import React, {
  useState,
  useEffect,
  useContext,
  useMemo,
} from "react";

import LayoutWrapper from "../../components/LayoutWrapper";
import HeaderBar from "../../components/HeaderBar";

import {
  FaSearch,
  FaPrint,
  FaFilter,
  FaTimes,
  FaDownload,
  FaCar,
  FaGasPump,
  FaRoad,
} from "react-icons/fa";

import {
  Fuel,
} from "lucide-react";

import {
  ToastContainer,
  toast,
} from "react-toastify";

import "react-toastify/dist/ReactToastify.css";

import axios from "axios";

import {
  useNavigate,
} from "react-router-dom";

import {
  ThemeContext,
} from "../../context/ThemeContext";

import getCurrentSkin from "../../utils/getCurrentSkin";

const API = "https://nissi-india.com/api/hsdhr";

const VehicleHsdFillingReport = () => {
  /* =========================================================
     THEME
  ========================================================= */

  const {
    isDark,
    skin,
    setSkin,
  } = useContext(
    ThemeContext
  );

  const currentSkin =
    getCurrentSkin(
      skin
    );

  const {
    baseColor,
    baseShade,
  } = currentSkin;

  const navigate =
    useNavigate();

  /* =========================================================
     LOCAL STORAGE
  ========================================================= */

  const finYearId =
    localStorage.getItem(
      "finYearId"
    ) || "";

  const finYear =
    localStorage.getItem(
      "finYear"
    ) || "";

  const storedBranchId =
    localStorage.getItem(
      "branchId"
    ) || "";

  const storedBranchType =
    localStorage.getItem(
      "branchType"
    ) || "HO";

  const userId =
    localStorage.getItem(
      "userId"
    ) || "";

  /* =========================================================
     COMMON HEADERS
  ========================================================= */

  const commonHeaders =
    useMemo(
      () => ({
        "x-finyearid":
          finYearId,

        "x-finyear":
          finYear,

        "x-branchid":
          storedBranchId,

        "x-branch":
          storedBranchType,

        "x-userid":
          userId,
      }),
      [
        finYearId,
        finYear,
        storedBranchId,
        storedBranchType,
        userId,
      ]
    );

  /* =========================================================
     UI STATE
  ========================================================= */

  const [
    showFilters,
    setShowFilters,
  ] = useState(true);

  const [
    loading,
    setLoading,
  ] = useState(false);

  /* =========================================================
     FILTER STATE
  ========================================================= */

  const [
    branchType,
    setBranchType,
  ] = useState(
    storedBranchType || "HO"
  );

  const [
    branchId,
    setBranchId,
  ] = useState(
    storedBranchId || "8"
  );

  const [
    fromDate,
    setFromDate,
  ] = useState("");

  const [
    toDate,
    setToDate,
  ] = useState("");

  const [
    vehicleRegNo,
    setVehicleRegNo,
  ] = useState("");

  const [
    vehicleCode,
    setVehicleCode,
  ] = useState("");

  const [
    vehicleName,
    setVehicleName,
  ] = useState("");

  const [
    vehicleMainType,
    setVehicleMainType,
  ] = useState("");

  // HO users default to All Branches; branch users are locked to their branch
  const [
    selectedBranch,
    setSelectedBranch,
  ] = useState(
    storedBranchType === "HO"
      ? ""
      : storedBranchId
  );

  // Own / Hire — independent of the user's HO/Branch level
  const [
    vehicleType,
    setVehicleType,
  ] = useState("O");

  const [
    month,
    setMonth,
  ] = useState("");

  const [
    year,
    setYear,
  ] = useState("");

  /* =========================================================
     DATA
  ========================================================= */

  const [
    branchList,
    setBranchList,
  ] = useState([]);

  const [
    yearList,
    setYearList,
  ] = useState([]);

  const [
    data,
    setData,
  ] = useState([]);

  /* =========================================================
     MONTH OPTIONS
  ========================================================= */

  const monthOptions = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  /* =========================================================
     DESIGN CLASSES
  ========================================================= */

  const inputClass = `
    w-full
    px-3
    py-2.5
    rounded-lg
    border
    text-sm
    transition-colors
    focus:outline-none
    focus:ring-2
    focus:ring-${baseColor}-500/30
    focus:border-${baseColor}-500
    ${
      isDark
        ? "bg-slate-800/60 border-slate-600/50 text-white placeholder-slate-400"
        : "bg-white border-slate-300 text-slate-900 placeholder-slate-400"
    }
  `;

  const labelClass = `
    block
    text-xs
    font-semibold
    tracking-wide
    mb-1.5
    ${
      isDark
        ? "text-slate-300"
        : "text-slate-700"
    }
  `;

  /* =========================================================
     FETCH BRANCH LIST
     CORE FUNCTIONALITY UNCHANGED
  ========================================================= */

  useEffect(() => {
    setLoading(true);

    axios
      .get(
        `${API}/fillbranch_hsdRpt`,
        {
                    params: {
            branchid:
              storedBranchId,

            branchtype:
              storedBranchType,
          },

          headers:
            commonHeaders,
        }
      )
      .then((res) => {
        const branches =
          res.data.branchList ||
          [];

              setBranchList(
          branches
        );
      })
      .catch((err) => {
        console.error(
          "Dropdown fetch failed",
          err
        );

        toast.error(
          "Failed to load branches"
        );
      })
      .finally(() => {
        setLoading(false);
      });
  }, [
    storedBranchId,
    storedBranchType,
    commonHeaders,
  ]);

  /* =========================================================
     FETCH YEARS
     CORE FUNCTIONALITY UNCHANGED
  ========================================================= */

  useEffect(() => {
    axios
      .get(
        `${API}/hsd_years`,
        {
          headers:
            commonHeaders,
        }
      )
      .then((res) => {
        setYearList(
          res.data || []
        );
      })
      .catch((err) => {
        console.error(
          "Years fetch failed",
          err
        );
      });
  }, [commonHeaders]);

  /* =========================================================
     DATE FORMAT
  ========================================================= */

  const formatDate = (
    dateStr
  ) => {
    if (!dateStr) {
      return "";
    }

    const [
      yearValue,
      monthValue,
      day,
    ] = dateStr.split("-");

    return `${day}/${monthValue}/${yearValue}`;
  };

  /* =========================================================
     SEARCH
     CORE FUNCTIONALITY UNCHANGED
  ========================================================= */

  const handleSearch = () => {
    if (
      !fromDate ||
      !toDate
    ) {
      toast.error(
        "Please select From Date and To Date"
      );

      return;
    }

    setLoading(true);

      /* =========================================================
     REPORT PARAMS (shared by Search + Print)
  ========================================================= */

  const buildReportParams = () => ({
    branchid: storedBranchId,
    branchtype: storedBranchType,

    // "" = All Branches (HO only) → '%' in the SP
    searchBranch:
      selectedBranch ||
      (storedBranchType === "HO"
        ? "%"
        : storedBranchId),

    vehicleCode: vehicleCode || "",
    vehicleName: vehicleName || "",
    vehicleRegNo: vehicleRegNo || "",

    fromDate: formatDate(fromDate),
    toDate: formatDate(toDate),

    month: month || "Select",
    year: year || "Select",

    vehicletypeIndex: "Select",
    vehicleType,
  });

    const params = buildReportParams();

    axios
      .get(
        `${API}/hsd_filling_report`,
        {
          params,

          headers:
            commonHeaders,
        }
      )
      .then((res) => {
        const responseData =
          res.data.data &&
          Array.isArray(
            res.data.data
          )
            ? res.data.data
            : [];

        setData(
          responseData
        );

        toast.success(
          `Loaded ${responseData.length} records`
        );
      })
      .catch((err) => {
        console.error(
          "HSD Report fetch failed",
          err
        );

        toast.error(
          "Failed to fetch HSD report"
        );

        setData([]);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  /* =========================================================
     PRINT
     CORE FUNCTIONALITY UNCHANGED
  ========================================================= */

  const handlePrint = () => {
    const params = {
      branchid:
        storedBranchId ||
        branchId ||
        8,

      branchtype:
        branchType ||
        storedBranchType ||
        "HO",

      searchBranch:
        selectedBranch ||
        branchId ||
        storedBranchId ||
        8,

      vehicleCode:
        vehicleCode || "",

      vehicleName:
        vehicleName || "",

      vehicleRegNo:
        vehicleRegNo || "",

      fromDate:
        formatDate(
          fromDate
        ),

      toDate:
        formatDate(
          toDate
        ),

      month:
        month ||
        "Select",

      year:
        year ||
        "Select",

      vehicletypeIndex:
        "Select",

      vehicleType:
        branchType === "HO"
          ? "O"
          : "H",
    };

    const query =
      new URLSearchParams(
        params
      ).toString();

    window.open(
      `/inventory/vehiclehsdfillingReport_AllPrint?${query}`,
      "_blank"
    );
  };

  /* =========================================================
     CLEAR FILTERS
     CORE FUNCTIONALITY UNCHANGED
  ========================================================= */

  const clearFilters = () => {
    setBranchType(
      storedBranchType ||
        "HO"
    );

    setBranchId(
      storedBranchId ||
        "8"
    );

    setFromDate("");

    setToDate("");

    setVehicleRegNo("");

    setVehicleCode("");

    setVehicleName("");

    setVehicleMainType("");

    setSelectedBranch(
      storedBranchType === "HO"
        ? ""
        : storedBranchId
    );

    setVehicleType("O");
    setMonth("");

    setYear("");

    setData([]);
  };

  /* =========================================================
     EXPORT
     ORIGINAL SECOND PAGE HAS ONLY VISUAL EXPORT BUTTON
  ========================================================= */

  const handleExport = () => {
    toast.info(
      "Export functionality is not implemented in the existing Vehicle HSD Filling Report."
    );
  };

  /* =========================================================
     NUMBER FORMAT
  ========================================================= */

  const formatNumber = (
    value
  ) => {
    return value
      ? parseFloat(
          value
        ).toLocaleString(
          "en-IN",
          {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          }
        )
      : "0.00";
  };

  /* =========================================================
     UI
  ========================================================= */

  return (
    <LayoutWrapper>

      {/* =====================================================
          PAGE HEADER
      ===================================================== */}

      <HeaderBar
        title={
          <span className="font-dmserif text-3xl">
            Vehicle HSD Filling HR/KM Report
          </span>
        }
        setSkin={
          setSkin
        }
        currentSkin={
          skin
        }
      />

      <div className="space-y-6 mt-6">

        {/* ===================================================
            FILTER CARD
            SAME DESIGN AS FIRST PAGE
        =================================================== */}

        <div
          className={`rounded-2xl shadow-xl border overflow-hidden ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10"
              : `bg-white ${currentSkin.glass} border-gray-200`
          }`}
        >

          {/* ===============================================
              HEADER
          =============================================== */}

          <div
            className={`px-6 py-4 border-b flex flex-col sm:flex-row sm:items-center justify-between gap-4 ${
              isDark
                ? "border-slate-700/50 bg-slate-900/40"
                : "border-slate-200/50 bg-white/60"
            }`}
          >

            {/* LEFT */}

            <div className="flex items-center gap-3">

              <div
                className={`flex items-center justify-center w-10 h-10 rounded-xl bg-gradient-to-br from-${baseColor}-500 to-${baseColor}-600 shadow-md`}
              >
                <Fuel className="w-5 h-5 text-white" />
              </div>

              <div>

                <h3
                  className={`text-lg font-bold ${
                    isDark
                      ? "text-white"
                      : "text-slate-900"
                  }`}
                >
                  Vehicle HSD Filling Report
                </h3>

                <p
                  className={`text-xs ${
                    isDark
                      ? "text-slate-400"
                      : "text-slate-600"
                  }`}
                >
                  Track and analyze vehicle fuel consumption and performance metrics
                </p>
              </div>
            </div>

            {/* =============================================
                EXPORT + FILTER
            ============================================= */}

            <div className="flex flex-wrap items-center gap-3">

              {/* EXPORT */}

              <button
                type="button"
                onClick={
                  handleExport
                }
                className={`inline-flex items-center gap-2 px-4 py-2 text-white text-sm font-semibold rounded-lg shadow transition bg-${baseColor}-${baseShade} hover:bg-${baseColor}-700`}
              >
                <FaDownload
                  size={14}
                />

                Export
              </button>

              {/* FILTER */}

              <button
                type="button"
                onClick={() =>
                  setShowFilters(
                    !showFilters
                  )
                }
                className={`inline-flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-lg border transition ${
                  isDark
                    ? "border-slate-600 text-slate-300 hover:bg-slate-800"
                    : "border-slate-300 text-slate-700 hover:bg-slate-100"
                }`}
              >
                <FaFilter
                  size={14}
                />

                {showFilters
                  ? "Hide Filters"
                  : "Show Filters"}
              </button>
            </div>
          </div>

          {/* ===============================================
              VEHICLE TYPE
          =============================================== */}

          <div
            className={`px-6 py-5 border-b ${
              isDark
                ? "border-slate-700/50"
                : "border-slate-200"
            }`}
          >

            <label
              className={
                labelClass
              }
            >
              Vehicle Type
            </label>

            <div className="flex flex-wrap gap-3 mt-2">

              {/* OWN VEHICLE */}

              <button
                type="button"
                                onClick={() =>
                  setVehicleType("O")
                }
                className={`px-5 py-2.5 rounded-lg text-sm font-semibold border transition ${
                  vehicleType ===
                  "O"
                    ? `bg-${baseColor}-${baseShade} text-white border-${baseColor}-${baseShade} shadow`
                    : isDark
                    ? "bg-slate-800 border-slate-600 text-slate-300 hover:bg-slate-700"
                    : "bg-white border-slate-300 text-slate-700 hover:bg-slate-100"
                }`}
              >
                Own Vehicle
              </button>

              {/* HIRE VEHICLE */}

              <button
                type="button"
                                onClick={() =>
                  setVehicleType("H")
                }
                className={`px-5 py-2.5 rounded-lg text-sm font-semibold border transition ${
                  vehicleType ===
                  "H"
                    ? `bg-${baseColor}-${baseShade} text-white border-${baseColor}-${baseShade} shadow`
                    : isDark
                    ? "bg-slate-800 border-slate-600 text-slate-300 hover:bg-slate-700"
                    : "bg-white border-slate-300 text-slate-700 hover:bg-slate-100"
                }`}
              >
                Hire Vehicle
              </button>
            </div>
          </div>

          {/* ===============================================
              FILTER BODY
          =============================================== */}

          {showFilters && (
            <div className="p-6">

              {/* =============================================
                  FILTER GRID
              ============================================= */}

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">

                {/* FROM DATE */}

                <div>

                  <label
                    className={
                      labelClass
                    }
                  >
                    From Date{" "}
                    <span className="text-red-500">
                      *
                    </span>
                  </label>

                  <input
                    type="date"
                    value={
                      fromDate
                    }
                    onChange={(e) =>
                      setFromDate(
                        e.target.value
                      )
                    }
                    className={
                      inputClass
                    }
                    required
                  />
                </div>

                {/* TO DATE */}

                <div>

                  <label
                    className={
                      labelClass
                    }
                  >
                    To Date{" "}
                    <span className="text-red-500">
                      *
                    </span>
                  </label>

                  <input
                    type="date"
                    value={
                      toDate
                    }
                    onChange={(e) =>
                      setToDate(
                        e.target.value
                      )
                    }
                    className={
                      inputClass
                    }
                    required
                  />
                </div>

                {/* REGISTRATION NO */}

                <div>

                  <label
                    className={
                      labelClass
                    }
                  >
                    Vehicle Registration No
                  </label>

                  <input
                    type="text"
                    value={
                      vehicleRegNo
                    }
                    onChange={(e) =>
                      setVehicleRegNo(
                        e.target.value
                      )
                    }
                    className={
                      inputClass
                    }
                    placeholder="Enter registration number"
                  />
                </div>

                {/* VEHICLE CODE */}

                <div>

                  <label
                    className={
                      labelClass
                    }
                  >
                    Vehicle Code
                  </label>

                  <input
                    type="text"
                    value={
                      vehicleCode
                    }
                    onChange={(e) =>
                      setVehicleCode(
                        e.target.value
                      )
                    }
                    className={
                      inputClass
                    }
                    placeholder="Enter vehicle code"
                  />
                </div>

                {/* VEHICLE NAME */}

                <div>

                  <label
                    className={
                      labelClass
                    }
                  >
                    Vehicle Name
                  </label>

                  <input
                    type="text"
                    value={
                      vehicleName
                    }
                    onChange={(e) =>
                      setVehicleName(
                        e.target.value
                      )
                    }
                    className={
                      inputClass
                    }
                    placeholder="Enter vehicle name"
                  />
                </div>

                {/* BRANCH */}

                <div>

                  <label
                    className={
                      labelClass
                    }
                  >
                    Branch
                  </label>

                  <select
                    value={
                      selectedBranch
                    }
                    onChange={(e) =>
                      setSelectedBranch(
                        e.target.value
                      )
                    }
                    className={
                      inputClass
                    }
                  >
                    <option value="">
                      All Branches
                    </option>

                    {branchList.map(
                      (b) => (
                        <option
                          key={
                            b.fld_Branchid
                          }
                          value={
                            b.fld_Branchid
                          }
                        >
                          {
                            b.fld_BranchName
                          }
                        </option>
                      )
                    )}
                  </select>
                </div>

                {/* MONTH */}

                <div>

                  <label
                    className={
                      labelClass
                    }
                  >
                    Month
                  </label>

                  <select
                    value={
                      month
                    }
                    onChange={(e) =>
                      setMonth(
                        e.target.value
                      )
                    }
                    className={
                      inputClass
                    }
                  >
                    <option value="">
                      Select Month
                    </option>

                    {monthOptions.map(
                      (m) => (
                        <option
                          key={
                            m
                          }
                          value={
                            m
                          }
                        >
                          {m}
                        </option>
                      )
                    )}
                  </select>
                </div>

                {/* YEAR */}

                <div>

                  <label
                    className={
                      labelClass
                    }
                  >
                    Year
                  </label>

                  <select
                    value={
                      year
                    }
                    onChange={(e) =>
                      setYear(
                        e.target.value
                      )
                    }
                    className={
                      inputClass
                    }
                  >
                    <option value="">
                      Select Year
                    </option>

                    {yearList.map(
                      (y) => (
                        <option
                          key={
                            y.value
                          }
                          value={
                            y.value
                          }
                        >
                          {
                            y.text
                          }
                        </option>
                      )
                    )}
                  </select>
                </div>
              </div>

              {/* =============================================
                  ACTIONS
              ============================================= */}

              <div className="flex flex-wrap justify-end gap-3 mt-6 pt-5 border-t border-slate-200 dark:border-slate-700">

                {/* CLEAR */}

                <button
                  type="button"
                  onClick={
                    clearFilters
                  }
                  className={`inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-xl border transition ${
                    isDark
                      ? "border-slate-600 text-slate-300 hover:bg-slate-800"
                      : "border-slate-300 text-slate-700 hover:bg-slate-100"
                  }`}
                >
                  <FaTimes
                    size={14}
                  />

                  Clear Filters
                </button>

                {/* PRINT */}

                <button
                  type="button"
                  onClick={
                    handlePrint
                  }
                  className={`inline-flex items-center gap-2 px-4 py-2.5 text-sm font-semibold rounded-xl transition ${
                    isDark
                      ? "bg-green-900/30 text-green-300 hover:bg-green-900/50"
                      : "bg-green-100 text-green-700 hover:bg-green-200"
                  }`}
                >
                  <FaPrint
                    size={14}
                  />

                  Print Report
                </button>

                {/* SEARCH */}

                <button
                  type="button"
                  onClick={
                    handleSearch
                  }
                  disabled={
                    loading
                  }
                  className={`inline-flex items-center gap-2 px-5 py-2.5 text-sm font-semibold text-white rounded-xl shadow transition bg-${baseColor}-600 hover:bg-${baseColor}-700 disabled:opacity-50`}
                >
                  {loading ? (
                    <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <FaSearch
                      size={14}
                    />
                  )}

                  {loading
                    ? "Loading..."
                    : "Search"}
                </button>
              </div>
            </div>
          )}
        </div>

        {/* ===================================================
            RESULTS CARD
        =================================================== */}

        <div
          className={`rounded-2xl shadow-xl border overflow-hidden ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10"
              : `bg-white ${currentSkin.glass} border-gray-200`
          }`}
        >

          {/* ===============================================
              RESULTS HEADER
          =============================================== */}

          <div className="p-6 border-b border-gray-200 dark:border-gray-700">

            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">

              <div className="flex items-center gap-3">

                <div
                  className={`w-3 h-8 rounded-full bg-${baseColor}-${baseShade}`}
                />

                <h3 className="text-xl font-bold">
                  Vehicle HSD Results
                </h3>

                <span
                  className={`px-3 py-1 rounded-full text-xs font-semibold ${
                    isDark
                      ? "bg-gray-700 text-gray-300"
                      : "bg-gray-100 text-gray-600"
                  }`}
                >
                  {data.length} records
                </span>
              </div>

              {data.length > 0 && (
                <span
                  className={`text-xs ${
                    isDark
                      ? "text-slate-400"
                      : "text-slate-500"
                  }`}
                >
                  Last updated:{" "}
                  {new Date().toLocaleTimeString()}
                </span>
              )}
            </div>
          </div>

          {/* ===============================================
              LOADING
          =============================================== */}

          {loading ? (
            <div className="flex flex-col items-center justify-center py-16">

              <div
                className={`w-8 h-8 border-4 border-${baseColor}-200 border-t-${baseColor}-600 rounded-full animate-spin`}
              />

              <p
                className={`mt-3 text-sm ${
                  isDark
                    ? "text-slate-400"
                    : "text-slate-500"
                }`}
              >
                Loading vehicle HSD records...
              </p>
            </div>
          ) : data.length > 0 ? (

            /* =============================================
               TABLE
            ============================================= */

            <div className="overflow-x-auto">

              <table className="w-full text-sm">

                <thead
                  className={`bg-gradient-to-r from-${baseColor}-600 to-${baseColor}-700 text-white`}
                >
                  <tr>

                    <th className="px-4 py-3 text-left text-xs font-bold uppercase">
                      S.No
                    </th>

                    <th className="px-4 py-3 text-left text-xs font-bold uppercase">
                      Vehicle Code
                    </th>

                    <th className="px-4 py-3 text-left text-xs font-bold uppercase">
                      Vehicle Name
                    </th>

                    <th className="px-4 py-3 text-left text-xs font-bold uppercase">
                      Reg No
                    </th>

                    <th className="px-4 py-3 text-right text-xs font-bold uppercase">
                      Working Hrs
                    </th>

                    <th className="px-4 py-3 text-right text-xs font-bold uppercase">
                      HSD (Hrs)
                    </th>

                    <th className="px-4 py-3 text-right text-xs font-bold uppercase">
                      Working Km
                    </th>

                    <th className="px-4 py-3 text-right text-xs font-bold uppercase">
                      HSD (Km)
                    </th>

                    <th className="px-4 py-3 text-right text-xs font-bold uppercase">
                      Diesel/Hr
                    </th>

                    <th className="px-4 py-3 text-right text-xs font-bold uppercase">
                      Diesel/Km
                    </th>

                    <th className="px-4 py-3 text-left text-xs font-bold uppercase">
                      Last Updated
                    </th>
                  </tr>
                </thead>

                <tbody
                  className={`divide-y ${
                    isDark
                      ? "divide-gray-600"
                      : "divide-gray-200"
                  }`}
                >
                  {data.map(
                    (
                      item,
                      index
                    ) => (
                      <tr
                        key={
                          index
                        }
                        className={`transition ${
                          isDark
                            ? "bg-gray-800/20 hover:bg-gray-700/30"
                            : "bg-white hover:bg-gray-50"
                        }`}
                      >

                        {/* SERIAL */}

                        <td className="px-4 py-3 font-medium">
                          {index + 1}
                        </td>

                        {/* VEHICLE CODE */}

                        <td className="px-4 py-3">

                          <span
                            className={`inline-block px-2.5 py-1 rounded-full text-xs font-semibold ${
                              isDark
                                ? "bg-gray-700 text-gray-200"
                                : `bg-${baseColor}-100 text-${baseColor}-800`
                            }`}
                          >
                            {item.fld_vehicle_code ||
                              "-"}
                          </span>
                        </td>

                        {/* VEHICLE NAME */}

                        <td
                          onClick={() =>
                            navigate(
                              "/inventory/Vehiclehsdfilling_Report1",
                              {
                                state: {
                                  row: item,
                                  filters: {
                                    fromDate,
                                    toDate,
                                    branch: selectedBranch,
                                    vehicleType,
                                    month,
                                    year,
                                  },
                                },
                              }
                            )
                          }
                          className={`px-4 py-3 font-medium cursor-pointer hover:underline ${
                            isDark
                              ? "text-blue-300"
                              : "text-blue-600"
                          }`}
                        >
                          {item.fld_vehicle_name ||
                            "-"}
                        </td>

                        {/* REG NO */}

                        <td className="px-4 py-3 whitespace-nowrap">
                          {item.fld_vehicle_regno ||
                            "-"}
                        </td>

                        {/* WORKING HOURS */}

                        <td className="px-4 py-3 whitespace-nowrap text-right">
                          {formatNumber(
                            item.fld_hsdfillingWorkinhr
                          )}
                        </td>

                        {/* HSD HOURS */}

                        <td className="px-4 py-3 whitespace-nowrap text-right">
                          {formatNumber(
                            item.fld_hsdfillinghr
                          )}
                        </td>

                        {/* WORKING KM */}

                        <td className="px-4 py-3 whitespace-nowrap text-right">
                          {formatNumber(
                            item.fld_hsdfillingWorkingKM
                          )}
                        </td>

                        {/* HSD KM */}

                        <td className="px-4 py-3 whitespace-nowrap text-right">
                          {formatNumber(
                            item.fld_hsdfillingkm
                          )}
                        </td>

                        {/* DIESEL / HR */}

                        <td
                          className={`px-4 py-3 whitespace-nowrap text-right font-semibold ${
                            isDark
                              ? "text-green-300"
                              : "text-green-700"
                          }`}
                        >
                          {formatNumber(
                            item.fld_diselconsumptionhr
                          )}
                        </td>

                        {/* DIESEL / KM */}

                        <td
                          className={`px-4 py-3 whitespace-nowrap text-right font-semibold ${
                            isDark
                              ? "text-green-300"
                              : "text-green-700"
                          }`}
                        >
                          {formatNumber(
                            item.fld_diselconsumptionkm
                          )}
                        </td>

                        {/* LAST UPDATED */}

                        <td className="px-4 py-3 whitespace-nowrap">
                          {item.fld_progressentrydate ||
                            "-"}
                        </td>
                      </tr>
                    )
                  )}
                </tbody>
              </table>
            </div>
          ) : (

            /* ===============================================
               EMPTY STATE
            =============================================== */

            <div className="px-4 py-14 text-center">

              <div className="flex justify-center">

                <FaCar
                  className={`h-12 w-12 mr-4 ${
                    isDark
                      ? "text-slate-500"
                      : "text-slate-400"
                  }`}
                />

                <FaGasPump
                  className={`h-12 w-12 mr-4 ${
                    isDark
                      ? "text-slate-500"
                      : "text-slate-400"
                  }`}
                />

                <FaRoad
                  className={`h-12 w-12 ${
                    isDark
                      ? "text-slate-500"
                      : "text-slate-400"
                  }`}
                />
              </div>

              <h3
                className={`mt-3 text-sm font-semibold ${
                  isDark
                    ? "text-slate-300"
                    : "text-slate-700"
                }`}
              >
                No vehicle HSD records found
              </h3>

              <p
                className={`mt-1 text-sm ${
                  isDark
                    ? "text-slate-500"
                    : "text-slate-500"
                }`}
              >
                Get started by searching with the filters above.
              </p>
            </div>
          )}
        </div>
      </div>

      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        closeOnClick
        pauseOnHover
        theme={
          isDark
            ? "dark"
            : "light"
        }
      />
    </LayoutWrapper>
  );
};

export default VehicleHsdFillingReport;